import { IconTrash } from '@tabler/icons-react';
import { AxiosError } from 'axios';

import { useEffect } from 'react';

import {
    Button,
    Center,
    Loader,
    Modal,
    Table,
    Text,
    Tooltip
} from '@mantine/core';

import { ProjectDto } from '../../api/models';
import {
    useDeleteApiProjectsId,
    useGetApiProjectsArchive,
    usePutApiProjectsArchiveRestoreId
} from '../../api/projects/projects.ts';
import ServerLoadErrorFallback from '../utils/ServerLoadErrorFallback.tsx';
import { useModal } from '../utils/useModal.tsx';
import { useProjectData } from '../utils/useProjectData.tsx';

interface ArchivedProjectsModalProps {
    opened: boolean;
    onClose: () => void;
}

const ArchivedProjectsModal = ({
    opened,
    onClose
}: ArchivedProjectsModalProps) => {
    const title = 'Archived Projects';
    const { refetchProjects } = useProjectData();
    const { showDeleteConfirmModal } = useModal();
    const { data, isLoading, refetch } = useGetApiProjectsArchive();
    const {
        mutateAsync: restoreProject,
        error: restoreError,
        isPending: isRestoring
    } = usePutApiProjectsArchiveRestoreId();
    const {
        mutateAsync: deleteProject,
        error: deleteError,
        isPending: isDeleting
    } = useDeleteApiProjectsId();

    useEffect(() => {
        if (opened) {
            void refetch();
        }
    }, [opened, refetch]);

    const handleRestore = async (project: ProjectDto) => {
        await restoreProject({ id: project.id });
        await refetch();
        await refetchProjects();
    };

    const handleDelete = (project: ProjectDto) => {
        showDeleteConfirmModal(
            'Are you sure?',
            'This project along with its documents will be deleted with no way to restore!',
            async () => {
                await deleteProject({ id: project.id });
                await refetch();
            }
        );
    };

    const sortedProjects = [...(data ?? [])].sort((a, b) => {
        const aClientName = a.client?.name ?? 'Unknown';
        const bClientName = b.client?.name ?? 'Unknown';
        const customerCompare = aClientName.localeCompare(bClientName);
        if (customerCompare !== 0) return customerCompare;

        const aName = a.name ?? 'Unknown';
        const bName = b.name ?? 'Unknown';
        return aName.localeCompare(bName);
    });

    const rows = sortedProjects?.map((project: ProjectDto) => (
        <Table.Tr key={project.id}>
            <Table.Td>{project.client?.name ?? 'Unknown'}</Table.Td>
            <Table.Td>{project.name}</Table.Td>
            <Table.Td>
                <Button
                    size='xs'
                    onClick={() => handleRestore(project)}
                    loading={isRestoring}
                    disabled={isRestoring || isDeleting}
                >
                    Restore
                </Button>
            </Table.Td>
            <Table.Td>
                <Tooltip label='Delete Project'>
                    <Button
                        onClick={() => handleDelete(project)}
                        loading={isDeleting}
                        disabled={isRestoring || isDeleting}
                        color='red'
                        variant='subtle'
                    >
                        <IconTrash size={18} />
                    </Button>
                </Tooltip>
            </Table.Td>
        </Table.Tr>
    ));

    return (
        <Modal opened={opened} onClose={onClose} title={title} size='xl'>
            {(restoreError ?? deleteError) && (
                <ServerLoadErrorFallback
                    entity={title}
                    error={(restoreError ?? deleteError) as AxiosError}
                />
            )}
            {isLoading ? (
                <Center py='md'>
                    <Loader />
                </Center>
            ) : data && data.length > 0 ? (
                <Table highlightOnHover withTableBorder>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Client</Table.Th>
                            <Table.Th>Project</Table.Th>
                            <Table.Th />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>{rows}</Table.Tbody>
                </Table>
            ) : (
                <Center py='md'>
                    <Text c='dimmed'>No archived projects</Text>
                </Center>
            )}
        </Modal>
    );
};

export default ArchivedProjectsModal;
